import { useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faXmark } from '@fortawesome/free-solid-svg-icons';
import { useAppContext } from '../../../context/AppContext';
import { fetchData } from '../../../services';
import {
    ASSOCIATED_OBJECT_LABELS,
    associatedObjectKey,
    formatAssociatedObject,
} from '../../../utils/tickets';

const extractList = (result, key, emptyMessages = []) => {
    if (Array.isArray(result?.[key])) return result[key];
    const message = String(result?.msg || result?.message || '').toLowerCase();
    if (emptyMessages.some((item) => message.includes(item.toLowerCase()))) {
        return [];
    }
    return [];
};

const objectOptionLabel = (type, item) => {
    if (type === 'design') {
        return `${item.design_name || 'Design'} (#${item.id})`;
    }
    if (type === 'quote') {
        return `Quote ${item.quote_number || item.id} (#${item.id})`;
    }
    if (type === 'invoice') {
        const siteName = item.project_site_name ? ` / ${item.project_site_name}` : '';
        return `Invoice ${item.id}${siteName}`;
    }
    if (type === 'upload') {
        return `${item.upload_name || 'Upload'} (#${item.id})`;
    }
    return `${ASSOCIATED_OBJECT_LABELS[type] || type} ${item.id}`;
};

export const AssociatedObjectsPicker = ({
    projectId,
    selectedObjects,
    onChange,
    disabled = false,
}) => {
    const { token, base_url, invoices, quotes } = useAppContext();
    const invoicesRef = useRef(invoices);
    const quotesRef = useRef(quotes);
    invoicesRef.current = invoices;
    quotesRef.current = quotes;
    const [available, setAvailable] = useState({
        design: [],
        quote: [],
        invoice: [],
        upload: [],
    });
    const [isLoading, setIsLoading] = useState(false);
    const [pendingKey, setPendingKey] = useState('');

    useEffect(() => {
        if (!projectId || !token) {
            setAvailable({ design: [], quote: [], invoice: [], upload: [] });
            setPendingKey('');
            return;
        }

        let cancelled = false;

        const loadRelatedObjects = async () => {
            setIsLoading(true);
            try {
                const [designsResult, specQuotesResult, fallbackQuotesResult, uploadsResult] = await Promise.all([
                    fetchData(token, `${base_url}/apis/designs/${projectId}`),
                    fetchData(token, `${base_url}/apis/billing/get-quotes/${projectId}`),
                    fetchData(token, `${base_url}/apis/billing?project_id=${projectId}`),
                    fetchData(token, `${base_url}/apis/uploads/get-uploads/${projectId}`),
                ]);

                if (cancelled) return;

                const designs = extractList(designsResult, 'designs');
                const specQuotes = extractList(specQuotesResult, 'quotes');
                const queryQuotes = extractList(fallbackQuotesResult, 'quotes');
                const contextQuotes = (quotesRef.current || []).filter(
                    (quote) => Number(quote.project_site_id) === Number(projectId)
                );
                const relatedQuotes = specQuotes.length
                    ? specQuotes
                    : queryQuotes.length
                        ? queryQuotes
                        : contextQuotes;
                const relatedInvoices = (invoicesRef.current || []).filter(
                    (invoice) => Number(invoice.project_site_id) === Number(projectId)
                );
                const uploads = extractList(uploadsResult, 'uploads', ['no uploads found']);

                setAvailable({
                    design: designs,
                    quote: relatedQuotes,
                    invoice: relatedInvoices,
                    upload: uploads,
                });
            } catch (error) {
                console.error('Error loading related ticket objects:', error);
                if (!cancelled) {
                    setAvailable({
                        design: [],
                        quote: (quotesRef.current || []).filter(
                            (quote) => Number(quote.project_site_id) === Number(projectId)
                        ),
                        invoice: (invoicesRef.current || []).filter(
                            (invoice) => Number(invoice.project_site_id) === Number(projectId)
                        ),
                        upload: [],
                    });
                }
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        loadRelatedObjects();

        return () => {
            cancelled = true;
        };
    }, [projectId, token, base_url]);

    const selectedKeys = useMemo(
        () => new Set((selectedObjects || []).map(associatedObjectKey)),
        [selectedObjects]
    );

    const options = useMemo(() => {
        const groups = [];
        Object.entries(available).forEach(([type, items]) => {
            const unused = (items || []).filter((item) => item?.id != null && !selectedKeys.has(`${type}:${item.id}`));
            if (unused.length === 0) return;
            groups.push({
                type,
                label: ASSOCIATED_OBJECT_LABELS[type],
                items: unused,
            });
        });
        return groups;
    }, [available, selectedKeys]);

    const addObject = () => {
        if (!pendingKey) return;
        const [type, idValue] = pendingKey.split(':');
        const id = Number(idValue);
        if (!type || !Number.isInteger(id)) return;
        if (selectedKeys.has(`${type}:${id}`)) return;
        onChange([...(selectedObjects || []), { type, id }]);
        setPendingKey('');
    };

    const removeObject = (object) => {
        const key = associatedObjectKey(object);
        onChange((selectedObjects || []).filter((item) => associatedObjectKey(item) !== key));
    };

    return (
        <div className="associated-objects-picker">
            {isLoading ? (
                <p className="form-helper-text">Loading related objects...</p>
            ) : (
                <div className="associated-objects-picker__row">
                    <select
                        id="ticket-associated-object"
                        className="form-select"
                        value={pendingKey}
                        disabled={disabled || options.length === 0}
                        onChange={(event) => setPendingKey(event.target.value)}
                    >
                        <option value="">
                            {!projectId
                                ? 'Select a project first'
                                : options.length === 0
                                    ? 'No related objects found'
                                    : 'Select a related object'}
                        </option>
                        {options.map((group) => (
                            <optgroup key={group.type} label={group.label}>
                                {group.items.map((item) => (
                                    <option key={`${group.type}:${item.id}`} value={`${group.type}:${item.id}`}>
                                        {objectOptionLabel(group.type, item)}
                                    </option>
                                ))}
                            </optgroup>
                        ))}
                    </select>
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={addObject}
                        disabled={disabled || !pendingKey}
                    >
                        <FontAwesomeIcon icon={faPlus} /> Add
                    </button>
                </div>
            )}

            {selectedObjects?.length > 0 && (
                <div className="ticket-object-chips">
                    {selectedObjects.map((object) => (
                        <span key={associatedObjectKey(object)} className="ticket-object-chip">
                            {formatAssociatedObject(object)}
                            {!disabled && (
                                <button
                                    type="button"
                                    className="ticket-object-chip__remove"
                                    onClick={() => removeObject(object)}
                                    aria-label={`Remove ${formatAssociatedObject(object)}`}
                                >
                                    <FontAwesomeIcon icon={faXmark} />
                                </button>
                            )}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
};
